import { BookOpen, Home, Plus, Sparkles } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { STRINGS } from '@/strings/strings';
import { useAuthStore } from '@/stores/authStore';

interface BottomNavProps {
  className?: string;
  onPlusClick: () => void;
}

export function BottomNav({ className, onPlusClick }: BottomNavProps) {
  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50 bg-dream-navy border-t border-stroke-on-dark',
        className,
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
    >
      <ContentContainer maxWidth="nav">
        <div className="relative flex h-[68px] items-end justify-between py-3.5 md:pt-4 md:pb-3 desktop:h-[72px] desktop:pt-4 desktop:pb-3.5">
          <div className="flex flex-1 items-center min-w-0 justify-between pr-7 md:justify-end md:gap-10 md:pr-8 desktop:pr-10">
            <BottomNavItem path="/" icon={Home} label={STRINGS.shell.nav.start} />
            <BottomNavItem path="/library" icon={BookOpen} label={STRINGS.shell.nav.library} />
          </div>
          <BottomNavSelectButton onClick={onPlusClick} />
          <div className="flex flex-1 items-center min-w-0 justify-between pl-7 md:justify-start md:gap-10 md:pl-8 desktop:pl-10">
            <BottomNavItem
              path="/inspiration"
              icon={Sparkles}
              label={STRINGS.shell.nav.inspiration}
            />
            <BottomNavProfileItem path="/account" label={STRINGS.shell.nav.account} />
          </div>
        </div>
      </ContentContainer>
    </nav>
  );
}

const navItemVariants = cva(
  'flex h-10 flex-col items-center justify-center gap-1 transition-opacity desktop:gap-2',
  {
    variants: {
      active: {
        true: 'opacity-100',
        false: 'opacity-50',
      },
    },
    defaultVariants: { active: false },
  },
);

const navLabelVariants = cva('text-center leading-none text-xxs md:text-xs desktop:text-sm', {
  variants: {
    active: {
      true: 'text-dream-gold',
      false: 'text-dream-white',
    },
  },
  defaultVariants: { active: false },
});

interface BottomNavItemProps extends VariantProps<typeof navItemVariants> {
  path: string;
  icon: typeof Home;
  label: string;
}

function BottomNavItem({ path, icon: Icon, label }: BottomNavItemProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === path;

  return (
    <button
      type="button"
      onClick={() => navigate(path)}
      aria-current={isActive ? 'page' : undefined}
      className={navItemVariants({ active: isActive })}
    >
      <Icon className="w-[22px] h-[22px] text-dream-white" strokeWidth={2} />
      <span className={navLabelVariants({ active: isActive })}>{label}</span>
    </button>
  );
}

interface BottomNavProfileItemProps {
  path: string;
  label: string;
}

function BottomNavProfileItem({ path, label }: BottomNavProfileItemProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isActive = location.pathname === path;

  // TODO(profile-image): render uploaded photo when backend adds User.profileImageUrl (post-launch).
  const initial = (user?.firstName ?? '').charAt(0).toUpperCase() || '?';

  return (
    <button
      type="button"
      onClick={() => navigate(path)}
      aria-current={isActive ? 'page' : undefined}
      aria-label={label}
      className={navItemVariants({ active: isActive })}
    >
      <span
        data-testid="bottomnav-profile-initial"
        className="flex h-[22px] w-[22px] items-center justify-center overflow-hidden rounded-full bg-dream-white text-xs leading-none text-dream-navy"
      >
        {initial}
      </span>
      <span className={navLabelVariants({ active: isActive })}>{label}</span>
    </button>
  );
}

interface BottomNavSelectButtonProps {
  onClick: () => void;
}

/**
 * Workstream F.9 — Haptic-Premium central "+" (variant c, locked
 * 2026-04-30). CSS-rendered to enable multi-layer shadow + gradient
 * + press-animation; the prior flat-gold SVG asset
 * (/icons/bottom-bar-button-select.svg) is no longer referenced
 * (file kept in /public for reference).
 *
 * Visual recipe:
 * - bg-gradient-to-b from-[#FFD86B] to-[#E5AB2D]: lighter gold at the
 *   top, darker gold at the bottom — produces the round-3D "polished
 *   coin" feel.
 * - Multi-layer shadow:
 *     * inset_0_2px_4px_rgba(255,255,255,0.45) — inner top highlight
 *     * 0_4px_12px_rgba(0,0,0,0.18)            — outer drop shadow
 *     * 0_0_24px_rgba(255,201,77,0.30)         — ambient gold halo
 * - 1px border at darker gold/40 — crisp edge against dark navbar bg.
 * - active:scale-95 + reduced shadow on press — haptic "click" feel.
 */
function BottomNavSelectButton({ onClick }: BottomNavSelectButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={STRINGS.shell.nav.new}
      className="shrink-0 flex h-[72px] w-[72px] items-center justify-center rounded-full border border-[#B8851F]/40 bg-gradient-to-b from-[#FFD86B] to-[#E5AB2D] text-dream-navy shadow-[inset_0_2px_4px_rgba(255,255,255,0.45),0_4px_12px_rgba(0,0,0,0.18),0_0_24px_rgba(255,201,77,0.30)] transition-transform duration-100 ease-out active:scale-95 active:shadow-[inset_0_2px_4px_rgba(255,255,255,0.45),0_2px_6px_rgba(0,0,0,0.15),0_0_16px_rgba(255,201,77,0.20)]"
    >
      <Plus className="h-8 w-8" strokeWidth={2.5} aria-hidden />
    </button>
  );
}
